/**
 * Void confirmation dialog (corrections Phase 3a — "Void & Redo").
 *
 * One dialog serves both flavours:
 *   - mode 'void':  plain void — reversal entries are added, nothing deleted.
 *   - mode 'redo':  void, then the caller reopens the create dialog
 *                   PREFILLED with the same values so the owner submits a
 *                   corrected entry.
 *
 * Shows both affected parties' balance before/after the void (computed
 * exactly with BigInt string math so the preview matches the server's
 * Decimal result). Refuses already-voided records; reversal rows never get
 * this dialog in the first place (callers filter them out).
 */
import { useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import type { Adjustment, Party, PartyBalance, Payment } from '../api';
import { Button, ErrorBanner, Spinner, TextArea } from '../ui/primitives';
import { Modal } from '../ui/Modal';
import { usePinGate } from '../auth/pinGate';
import { addKinrStrings, kinrToInrDisplay, negateKinrString } from '../money/inr';

export interface VoidRedoTarget {
  kind: 'payment' | 'adjustment';
  id: number;
  mode: 'void' | 'redo';
}

export interface VoidRedoDialogProps {
  target: VoidRedoTarget;
  onClose: () => void;
  /**
   * Called once the void has landed. For mode 'redo' the caller opens the
   * matching create dialog prefilled from the returned record.
   */
  onVoided: (record: { payment?: Payment; adjustment?: Adjustment }) => void;
}

interface AffectedParty {
  partyId: number;
  /** Signed K-INR change the void applies to this party's balance. */
  delta: string;
}

export function VoidRedoDialog({ target, onClose, onVoided }: VoidRedoDialogProps) {
  const qc = useQueryClient();
  const { withPin } = usePinGate();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isPayment = target.kind === 'payment';

  const paymentQ = useQuery<Payment>({
    queryKey: ['payment', target.id],
    queryFn: () => api.get<Payment>(`/api/payments/${target.id}`),
    enabled: isPayment,
  });
  const adjustmentQ = useQuery<Adjustment>({
    queryKey: ['adjustment', target.id],
    queryFn: () => api.get<Adjustment>(`/api/adjustments/${target.id}`),
    enabled: !isPayment,
  });
  const partiesQ = useQuery<Party[]>({
    queryKey: ['parties'],
    queryFn: () => api.get<Party[]>('/api/parties'),
  });

  const record: Payment | Adjustment | undefined = isPayment
    ? paymentQ.data
    : adjustmentQ.data;
  const loading = (isPayment ? paymentQ.isLoading : adjustmentQ.isLoading) || partiesQ.isLoading;

  const partiesById = useMemo(
    () => new Map((partiesQ.data ?? []).map((p) => [p.id, p])),
    [partiesQ.data],
  );
  const selfId = useMemo(
    () => (partiesQ.data ?? []).find((p) => p.role === 'self')?.id ?? null,
    [partiesQ.data],
  );

  // Voiding adds reversal entries (Dr/Cr swapped), so each party's balance
  // moves by the OPPOSITE of the original entry's effect:
  //   payment:    payer leg was Cr amount → +amount; payee leg was Dr → −amount.
  //   adjustment: dr leg was Dr amount    → −amount; cr leg was Cr    → +amount.
  const affected: AffectedParty[] = useMemo(() => {
    if (!record) return [];
    if (isPayment) {
      const p = record as Payment;
      return [
        { partyId: p.payerPartyId, delta: p.amount },
        { partyId: p.payeePartyId, delta: negateKinrString(p.amount) },
      ];
    }
    const a = record as Adjustment;
    const dr = a.drPartyId ?? selfId;
    const cr = a.crPartyId ?? selfId;
    const out: AffectedParty[] = [];
    if (dr !== null) out.push({ partyId: dr, delta: negateKinrString(a.amount) });
    if (cr !== null) out.push({ partyId: cr, delta: a.amount });
    return out;
  }, [record, isPayment, selfId]);

  const balanceQueries = useQueries({
    queries: affected.map((p) => ({
      queryKey: ['balance', p.partyId],
      queryFn: () => api.get<PartyBalance>(`/api/parties/${p.partyId}/balance`),
    })),
  });

  const alreadyVoided = record?.voidedAt != null;
  const kindLabel = isPayment ? 'payment' : 'adjustment';
  const title =
    target.mode === 'redo' ? `Void & re-enter this ${kindLabel}` : `Void this ${kindLabel}`;

  const voidMut = useMutation({
    mutationFn: (r: string) =>
      api.post(
        `/api/${isPayment ? 'payments' : 'adjustments'}/${target.id}/void`,
        { reason: r },
      ),
    onSuccess: () => {
      void qc.invalidateQueries();
      onVoided(
        isPayment ? { payment: paymentQ.data } : { adjustment: adjustmentQ.data },
      );
    },
    onError: (e) => {
      // 423 means the PIN modal is taking over — not an error to show.
      if (e instanceof ApiError && e.status === 423) return;
      setError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const confirm = () => {
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      setError('Please say why you are voiding this — it goes on the reversal entries.');
      return;
    }
    setError(null);
    void withPin(() => voidMut.mutateAsync(trimmed));
  };

  return (
    <Modal onClose={onClose} size="lg">
      <header className="flex items-center justify-between border-b border-border bg-surface-raised px-4 py-3">
        <h2 className="text-small font-semibold text-ink">{title}</h2>
        <Button variant="ghost" onClick={onClose}>Close</Button>
      </header>

      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex items-center gap-2 text-small text-ink-muted">
            <Spinner /> loading…
          </div>
        ) : !record ? (
          <ErrorBanner>Could not load the {kindLabel}.</ErrorBanner>
        ) : (
          <div className="space-y-4">
            {alreadyVoided && (
              <ErrorBanner>
                This {kindLabel} was already voided on {record.voidedAt!.slice(0, 10)}
                {record.voidedReason ? ` — “${record.voidedReason}”` : ''}. Nothing more to undo.
              </ErrorBanner>
            )}

            {/* What is being voided */}
            <div className="rounded-md border border-border bg-surface-raised p-3 text-small">
              {isPayment ? (
                <PaymentSummary payment={record as Payment} partiesById={partiesById} />
              ) : (
                <AdjustmentSummary adjustment={record as Adjustment} partiesById={partiesById} />
              )}
            </div>

            {/* Balance before/after */}
            <div>
              <h3 className="mb-2 text-small font-semibold text-ink">
                What happens to balances
              </h3>
              <ul className="space-y-2">
                {affected.map((p, i) => {
                  const before = balanceQueries[i]?.data?.balance;
                  const after =
                    before !== undefined ? addKinrStrings(before, p.delta) : null;
                  return (
                    <li
                      key={p.partyId}
                      className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface px-2.5 py-1.5 text-small"
                    >
                      <span className="min-w-0 truncate font-medium text-ink">
                        {partiesById.get(p.partyId)?.displayName ?? `#${p.partyId}`}
                      </span>
                      <span className="shrink-0 font-mono text-ink">
                        {before ?? '…'}
                        <span className="mx-1.5 text-ink-muted">→</span>
                        {after ?? '…'}
                        <span className="ml-1 text-caption text-ink-muted">K-INR</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-1.5 text-caption text-ink-muted">
                Nothing is deleted — matching reversal entries are added, so the
                books stay auditable.
                {target.mode === 'redo' &&
                  ' After this, the form reopens prefilled so you can save the corrected version.'}
              </p>
            </div>

            {!alreadyVoided && (
              <TextArea
                label="Why is this being voided?"
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Wrong amount — should be 45,000 not 54,000"
              />
            )}

            {error && <ErrorBanner>{error}</ErrorBanner>}
          </div>
        )}
      </div>

      <footer className="flex items-center justify-end gap-2 border-t border-border bg-surface-raised px-4 py-3">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        {!alreadyVoided && record && (
          <Button variant="danger" onClick={confirm} disabled={voidMut.isPending}>
            {voidMut.isPending ? (
              <Spinner />
            ) : target.mode === 'redo' ? (
              'Void, then re-enter'
            ) : (
              `Void ${kindLabel}`
            )}
          </Button>
        )}
      </footer>
    </Modal>
  );
}

function PaymentSummary({
  payment,
  partiesById,
}: {
  payment: Payment;
  partiesById: Map<number, Party>;
}) {
  return (
    <>
      <p className="font-medium text-ink">
        {partiesById.get(payment.payerPartyId)?.displayName ?? '—'}
        {' → '}
        {partiesById.get(payment.payeePartyId)?.displayName ?? '—'}
      </p>
      <p className="mt-1 text-ink-muted">
        <span className="font-mono text-ink">{payment.amount} K-INR</span>
        <span className="ml-1.5">({kinrToInrDisplay(payment.amount)})</span>
        {' · '}
        {payment.method.replace('_', ' ')} · {payment.effectiveDate}
        {payment.viaSelf ? ' · 3-party pass-through' : ''}
      </p>
      {payment.note && <p className="mt-1 text-ink-muted">“{payment.note}”</p>}
    </>
  );
}

function AdjustmentSummary({
  adjustment,
  partiesById,
}: {
  adjustment: Adjustment;
  partiesById: Map<number, Party>;
}) {
  const name = (id: number | null) =>
    id === null ? 'Self (auto-offset)' : partiesById.get(id)?.displayName ?? `#${id}`;
  return (
    <>
      <p className="font-medium text-ink">{adjustment.description}</p>
      <p className="mt-1 text-ink-muted">
        <span className="font-mono text-ink">{adjustment.amount} K-INR</span>
        <span className="ml-1.5">({kinrToInrDisplay(adjustment.amount)})</span>
        {' · '}
        Dr {name(adjustment.drPartyId)} / Cr {name(adjustment.crPartyId)} ·{' '}
        {adjustment.effectiveDate}
      </p>
      {adjustment.note && <p className="mt-1 text-ink-muted">“{adjustment.note}”</p>}
    </>
  );
}
